import { useEffect, useRef, useState } from 'react';

import type { Cart } from '@getjust/gateway';

import { useCart } from '$src/hooks/queries';

const CLEAR_AFTER_MS = 1500;

const eqCents = (a: number | undefined, b: number | undefined): boolean => {
  return Math.round((a ?? 0) * 100) === Math.round((b ?? 0) * 100);
};

const diffCart = (prev: Cart | undefined, next: Cart | undefined): Set<string> => {
  const changed = new Set<string>();
  if (!prev || !next) return changed;
  if (!eqCents(prev.totalAmount, next.totalAmount)) changed.add('totalAmount');
  if (!eqCents(prev.subTotal, next.subTotal)) changed.add('subTotal');
  if (!eqCents(prev.totalShipping, next.totalShipping)) changed.add('totalShipping');
  if (!eqCents(prev.totalDiscount, next.totalDiscount)) changed.add('totalDiscount');
  if (!eqCents(prev.totalTax, next.totalTax)) changed.add('totalTax');
  if (!eqCents(prev.totalDuties, next.totalDuties)) changed.add('totalDuties');
  return changed;
};

/**
 * Tracks which top-level cart fields changed between consecutive
 * useCart resolutions, returning a Set keyed by stable field names.
 * Entries auto-clear after 1500 ms so subsequent unrelated updates
 * (user-driven discount apply, etc.) don't replay the skeleton.
 *
 * The first useCart resolve has no previous cart, so changedFields is
 * empty (no skeleton flash on cold opens).
 */
export function useCartFieldDiff(): Set<string> {
  const { data: cart } = useCart();
  const prevRef = useRef<Cart | undefined>(undefined);
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!cart) return;
    const prev = prevRef.current;
    prevRef.current = cart;
    if (!prev) return;
    const diff = diffCart(prev, cart);
    if (diff.size === 0) return;
    setChangedFields(diff);
    const timer = setTimeout(() => setChangedFields(new Set()), CLEAR_AFTER_MS);
    return () => clearTimeout(timer);
  }, [cart]);

  return changedFields;
}
